// Accumulation indicators — the SHARED implementation used by both the interactive chart
// (app/chart/[symbol]/page.tsx) and the nightly Accumulation Score builder, so the number a user
// reads off the chart and the number that ranks the /accumulation board can never diverge. Pure
// functions, no IO. Extracted from the chart page 2026-09-02 (Phase 2 step 1); the math is unchanged.

// On-Balance Volume: running total that adds the bar's volume on an up-close, subtracts it on a
// down-close, and leaves it flat on an unchanged close. obv[0] = 0 (warm-up handled by the caller
// via NaN filtering). Returns one value per bar. Needs ≥2 bars.
export function calculateOBV(bars: { close: number; volume?: number }[]): number[] {
  const n = bars.length;
  const obv = new Array(n).fill(NaN);
  if (n < 2) return obv;
  obv[0] = 0;
  for (let i = 1; i < n; i++) {
    const v = bars[i].volume ?? 0;
    const c = bars[i].close;
    const pc = bars[i - 1].close;
    obv[i] = obv[i - 1] + (c > pc ? v : c < pc ? -v : 0);
  }
  return obv;
}

// Williams Accumulation/Distribution line: cumulative money-flow volume, where the money-flow
// multiplier ((close-low)-(high-close))/(high-low) weights each bar's volume by where it closed in
// its range. Cumulative from the first bar. Returns one value per bar. Needs ≥2 bars.
export function calculateADLine(bars: { high: number; low: number; close: number; volume?: number }[]): number[] {
  const n = bars.length;
  const ad = new Array(n).fill(NaN);
  if (n < 2) return ad;
  let cum = 0;
  for (let i = 0; i < n; i++) {
    const { high, low, close } = bars[i];
    const v = bars[i].volume ?? 0;
    const mfm = high === low ? 0 : ((close - low) - (high - close)) / (high - low);
    cum += mfm * v;
    ad[i] = cum;
  }
  return ad;
}

// Chaikin Money Flow (period 20): rolling sum of money-flow volume over rolling sum of volume, so it
// oscillates around 0 (positive = accumulation, negative = distribution). Warm-up bars (i<period-1)
// stay NaN — the caller filters them out, mirroring how RSI skips its warm-up.
export function calculateCMF(bars: { high: number; low: number; close: number; volume?: number }[], period = 20): number[] {
  const n = bars.length;
  const cmf = new Array(n).fill(NaN);
  if (n < period) return cmf;
  const mfv = new Array(n).fill(0);
  const vol = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    const { high, low, close } = bars[i];
    const v = bars[i].volume ?? 0;
    const mfm = high === low ? 0 : ((close - low) - (high - close)) / (high - low);
    mfv[i] = mfm * v;
    vol[i] = v;
  }
  for (let i = period - 1; i < n; i++) {
    let sumMFV = 0;
    let sumVol = 0;
    for (let j = i - period + 1; j <= i; j++) { sumMFV += mfv[j]; sumVol += vol[j]; }
    cmf[i] = sumVol > 0 ? sumMFV / sumVol : 0;
  }
  return cmf;
}

// Accumulation vs distribution DAY count over the last `lookback` bars: an accumulation day closes UP
// on rising volume, a distribution day closes DOWN on rising volume. Each bar compares to the prior
// one, so the window walks indices [n-lookback .. n-1] (clamped to ≥1). Returns null if <2 bars.
export function computeAccDist(bars: { close: number; volume?: number }[], lookback = 25): { acc: number; dist: number } | null {
  const n = bars.length;
  if (n < 2) return null;
  let acc = 0;
  let dist = 0;
  const start = Math.max(1, n - lookback);
  for (let i = start; i < n; i++) {
    const v = bars[i].volume ?? 0;
    const pv = bars[i - 1].volume ?? 0;
    if (bars[i].close > bars[i - 1].close && v > pv) acc++;
    else if (bars[i].close < bars[i - 1].close && v > pv) dist++;
  }
  return { acc, dist };
}
